import { getRequestEvent } from 'solid-js/web';
import { query, action, json } from '@solidjs/router';
import { getBasicInfo } from '@bz/trackpoint';
import { encode as base64Encode } from 'js-base64';
import { captureException } from '@bz/sentry';

import { env } from './env';
import { setting } from './setting';
import { ApiPaths, GetApis, PostApis } from './apis';
import postDepends from './postDepends.config';
import responseGuards from './responseGuards.config';

import { calculateSignature } from '~/security/signature';
import { decryptResponse } from '~/security/decrypt';
import { getSessionId } from '~/hook/initSession';
import { fetchSignature, SIGNATURE_COOKIE_NAME, SIGNATURE_MAX_AGE } from '~/utils/signature';
import {
  clearRecaptchaVerifyTokenFromCookie,
  ensureRecaptchaVerifyToken,
  getRecaptchaVerifyTokenFromCookie,
} from '~/utils/recaptchaVerify';
import { Events, emitEvtBus } from '~/hook/useEvtBus';
import { VERSION_CLEARED } from '~/constants';

/** Business error */
export class ServerError extends Error {
  constructor(
    public message: string,
    public code: number,
    public url: string,
    public raw?: any
  ) {
    super(message);
    this.code = code;
    this.url = url;
    this.raw = raw;

    // Mark this Error instance as a known business error; we use name to distinguish from network/system errors.
    this.name = 'ServerError';
  }
}

/** Network error */
export class NetworkError extends Error {
  constructor(message: string | Error) {
    super(message instanceof Error ? message.message : message);
    if (message instanceof Error) {
      this.stack = message.stack;
    }
  }
}

/** HTTP error */
export class HttpError extends Error {
  constructor(
    public code: number,
    error: string | Error,
    public url: string
  ) {
    super(error instanceof Error ? error.message : error);
    if (error instanceof Error) {
      this.stack = error.stack;
    }
    this.name = 'HttpError';
  }
}

function makeQuery(key: string, url: string, hasPathParams: boolean) {
  return query(async (_query: any, _path: any) => {
    let { source, query } = fillPathTemplate(url, hasPathParams, _query, _path);
    if (query) {
      source += `?${new URLSearchParams(query)}`;
    }
    return await checkResponseGuards(requestJSON(source, { method: 'get' }));
  }, `${key}`);
}

function makeAction(key: string, url: string, hasPathParams: boolean) {
  return action(async (_query: any, _path: any) => {
    let body: undefined | string;
    let { source, query } = fillPathTemplate(url, hasPathParams, _query, _path);

    let timeOut: number | undefined;

    if (query) {
      timeOut = query.timeOut;
      delete query.timeOut;

      body = JSON.stringify(query);
    }
    if (_path) {
      source += `?${new URLSearchParams(_path)}`;
    }
    const res = await checkResponseGuards(requestJSON(source, { method: 'post', body }, timeOut));
    return json(res, {
      revalidate: (postDepends as any)[key] || [],
    });
  }, `${key}`);
}

function makeJSONRequest<T>(method: typeof makeAction | typeof makeQuery) {
  const cache = new Map<string | symbol, any>();
  return new Proxy(
    {},
    {
      get(target, prop) {
        const instance = cache.get(prop);
        if (!instance) {
          const url: string = (ApiPaths as any)[prop];
          const hasPathParams = url.lastIndexOf('}') !== -1;
          const instance = method(prop as string, url, hasPathParams);
          cache.set(prop, instance);
          return instance;
        }
        return instance;
      },
    }
  ) as unknown as T;
}

/** Fetch backend data via GET */
export const get = makeJSONRequest<GetApis>(makeQuery); // Temporary i18n phase-2 workaround, needs optimization later
/** Fetch backend data via POST */
export const post = makeJSONRequest<PostApis>(makeAction); // Temporary i18n phase-2 workaround, needs optimization later

/**
 * Remove query cache so every call re-fetches
 * For cases where query params haven't changed but you need to bypass query cache
 * @param method
 * @returns
 */
function makeOnceRequest<T>(method: typeof makeAction | typeof makeQuery) {
  return new Proxy(
    {},
    {
      get(target, prop) {
        const url: string = (ApiPaths as any)[prop];
        const hasPathParams = url.lastIndexOf('}') !== -1;
        return method(`${String(prop)}-${Date.now()}`, url, hasPathParams);
      },
    }
  ) as unknown as T;
}
export const getOnce = makeOnceRequest<GetApis>(makeQuery);

function fillPathTemplate(url: string, hasPathParams: boolean, query: any, path: any) {
  let source = url;
  if (hasPathParams) {
    if (path === undefined) {
      path = query;
      query = undefined;
    }
    source = url.replace(/\{(\w+)\}/g, (_, key) => encodeURIComponent(path[key]));
  }
  return {
    source,
    query,
  };
}

// Default request timeout (60 seconds)
const TIME_OUT = 60e3;

// List of APIs that should be retried on failure
const RETRY_API_LIST = new Set<string>([
  // Add API paths that need retry here
  '/api/user/banners/display',
  // 'api/v1/sportData/match/search',
]);

/**
 * Check if the URL should be retried
 */
function shouldRetryUrl(url: string): boolean {
  return Array.from(RETRY_API_LIST).some((apiPath) => url.includes(apiPath));
}

/**
 * Execute the actual request
 * Accepts a timeOut parameter to allow custom timeout values
 */
async function executeRequest(
  url: string,
  init: RequestInit | undefined,
  headers: Headers,
  requestTimestamp: number,
  timeOut?: number
) {
  const apiUrl = env.origin + url;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(`Timeout: ${url}`), timeOut ?? TIME_OUT);
  const startTime = performance.now();

  // Get serverSeed for decryption
  let serverSeed = '';
  if (import.meta.env.SSR) {
    serverSeed = getRequestEvent()?.locals.signature || '';
  } else {
    serverSeed =
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('_signature_='))
        ?.split('=')[1] || '';
  }

  try {
    const res = await fetch(apiUrl, {
      ...init,
      headers,
      signal: controller.signal,
    }).finally(() => clearTimeout(id));

    if (res.ok) {
      const endTime = performance.now();
      ssrLog(`[responseJSON] ${url}`, 'request time', endTime - startTime);

      // Check if response is encrypted (Content-Type is text/plain with Base64 content)
      const contentType = res.headers.get('Content-Type');

      if (contentType?.includes('text/plain')) {
        // Response is encrypted, needs decryption
        const encryptedBase64 = await res.text();

        if (!serverSeed) {
          throw new Error('Missing serverSeed for decryption');
        }

        // Decrypt response
        const decryptedData = decryptResponse(encryptedBase64, serverSeed, requestTimestamp);

        // Check decrypted data
        if (!decryptedData.success) {
          throw new ServerError(decryptedData.errMessage, decryptedData.errCode, res.url, decryptedData);
        }

        return decryptedData.data;
      } else {
        // Unencrypted JSON response
        const json = await res.json();
        if (!json.success) {
          throw new ServerError(json.errMessage, json.errCode, res.url, json);
        }
        return json.data;
      }
    } else {
      ssrLog(`[request error] ${res.status} ${url}`);
      const text = await res.text();

      let errorData: any = null;

      // Try decrypting response first (compatible with both encrypted and unencrypted cases)
      try {
        if (serverSeed && text) {
          const decryptedData = decryptResponse(text, serverSeed, requestTimestamp);
          console.log('decryptedData', decryptedData);

          // If decryption succeeded and returned an object, use the decrypted data
          if (decryptedData && typeof decryptedData === 'object') {
            errorData = decryptedData;
          }
        }
      } catch (decryptError) {
        // Decryption failed, might be plain JSON, continue with direct parsing
        console.log('Decryption failed, try parsing as plain JSON');
      }

      // If decryption failed or was not attempted, try parsing JSON directly
      if (!errorData) {
        try {
          errorData = JSON.parse(text);
        } catch (parseError) {
          // JSON parsing also failed, throw HttpError
          const isHTML = typeof text === 'string' && (text.includes('<!DOCTYPE') || text.includes('<html'));

          if (isHTML) {
            throw new HttpError(res.status, 'Please check your internet connection and try again.', apiUrl);
          }
          throw new HttpError(res.status, text + `\n${apiUrl}`, apiUrl);
        }
      }

      // Check error code and throw corresponding error
      if (errorData.errCode) {
        throw new ServerError(errorData.errMessage || 'Request failed', errorData.errCode, res.url, errorData);
      } else {
        throw new HttpError(res.status, text + `\n${apiUrl}`, apiUrl);
      }
    }
  } finally {
    clearTimeout(id);
  }
}

export async function requestJSON(url: string, init?: RequestInit, timeOut?: number) {
  const { request, locals } = getRequestEvent() ?? {};
  const headers = new Headers(!locals?.[VERSION_CLEARED] ? request?.headers : undefined);

  ssrLog('[requestJSON]  Start', url);

  if (!import.meta.env.SSR) {
    const { deviceId, channelType } = getBasicInfo();
    headers.set('X-Device-Id', deviceId); // Device ID
    headers.set('X-Channel-Type', String(channelType || 0)); // Channel type
    headers.set('X-Front-Session-Id', getSessionId() || ''); // Session ID

    // Ensure verify token is obtained (from cookie or through verification flow)
    const verifyToken = getRecaptchaVerifyTokenFromCookie();

    // Use verify token as request header (if available)
    if (verifyToken) {
      headers.set('X-Recaptcha-Token', verifyToken);
    }
  }

  headers.set(
    'accept-language',
    untrack(() => setting.lang)
  );
  headers.set('content-type', 'application/json');
  headers.set('X-Client-Type', import.meta.env.VITE_APP_CLIENT);
  headers.set('accept', 'application/json');

  // X-Timestamp: current millisecond timestamp - saved for decryption
  const requestTimestamp = Date.now();
  headers.set('X-Timestamp', String(requestTimestamp));

  // Add X-Server-Seed signature
  if (import.meta.env.SSR) {
    // SSR: get signature from locals
    headers.set('X-Server-Seed', base64Encode(locals?.signature || ''));
  } else {
    // CSR: get signature from cookie
    const signature = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_signature_='))
      ?.split('=')[1];
    if (signature) {
      headers.set('X-Server-Seed', base64Encode(signature));

      // CSR also needs to add an X-Signature
      const { deviceId } = getBasicInfo();
      const method = init?.method || 'GET';

      // Extract path from full URL (without domain)
      const urlPath = url.startsWith('http') ? new URL(url).pathname : url;
      console.log('Calculating signature for:', urlPath);
      // Extract clean path, strip query parameters
      const pureUrl = urlPath.split('?')[0];

      const calculatedSignature = calculateSignature(signature, requestTimestamp, method, pureUrl, deviceId);

      headers.set('X-Signature', calculatedSignature);
    }
    // If no signature exists, don't set X-Server-Seed or X-Signature as it would cause signature verification failure

    // Add browser language
    headers.set('X-Browser-Language', navigator.language);
  }

  if (init?.headers) {
    Object.entries(init.headers).forEach(([key, value]) => headers.append(key, value));
  }

  // Determine if retry is needed; SSR doesn't need retry logic as it doesn't require reCAPTCHA verification
  const needRetry = shouldRetryUrl(url) && !import.meta.env.SSR;
  const maxRetries = needRetry ? 1 : 0; // Max 1 retry
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        ssrLog(`[requestJSON] Retry ${attempt}/${maxRetries} for`, url);
      }
      return await executeRequest(url, init, headers, requestTimestamp, timeOut);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      // If this is the last attempt, break the loop
      if (attempt === maxRetries) {
        break;
      }

      // Reaching here means we need to re-fetch; update the signature in headers by re-fetching it from the API
      let signature: string | null = null;
      // For CSR, re-fetch the signature
      if (!import.meta.env.SSR) {
        // Re-fetch signature
        clearRecaptchaVerifyTokenFromCookie();
        // Ensure verify token is obtained (from cookie or through verification flow)
        const verifyToken = await ensureRecaptchaVerifyToken(env.origin, {
          deviceId: headers.get('X-Device-Id') || '',
          channelType: headers.get('X-Channel-Type') || '0',
          sessionId: headers.get('X-Front-Session-Id') || '',
          lang: untrack(() => setting.lang),
        });

        // Use verify token as request header (if available)
        if (verifyToken) {
          headers.set('X-Recaptcha-Token', verifyToken);
        }

        signature = await fetchSignature(undefined, {
          'X-Device-Id': headers.get('X-Device-Id') || '',
          'X-Channel-Type': headers.get('X-Channel-Type') || '0',
          'X-Front-Session-Id': headers.get('X-Front-Session-Id') || '',
          'X-Recaptcha-Token': headers.get('X-Recaptcha-Token') || '',
        });
        if (signature) {
          headers.set('X-Server-Seed', base64Encode(signature));
          // Also update cookie (expires in 5 minutes)
          document.cookie = `${SIGNATURE_COOKIE_NAME}=${signature}; path=/; max-age=${SIGNATURE_MAX_AGE}`;
          const deviceId = headers.get('X-Device-Id') || '';
          const pureUrl = url.split('?')[0];
          const calculatedSignature = calculateSignature(
            signature,
            requestTimestamp,
            init?.method || 'GET',
            pureUrl,
            deviceId
          );
          headers.set('X-Signature', calculatedSignature);
        }
      }
    }
  }

  // Do NOT remove this
  throw lastError;
}

async function checkResponseGuards(source: Promise<any>) {
  try {
    return await source;
  } catch (err: HttpError | ServerError | any) {
    if (err instanceof ServerError || err instanceof HttpError) {
      captureException(
        err,
        {
          raw: err instanceof ServerError ? err.raw : undefined,
          message: err.message,
        },
        {
          // Tags for advanced filtering and searching in Sentry:
          errorCode: String(err.code),
          apiUrl: String(err.url),
        }
      );
    }

    // ✅ Handle token expiration errors first
    // ✅ Check if this is a token expiration error (compatible with multiple cases)
    // 1. err.code === 12000001/12000002 (original business error codes)
    // 2. err.code === 401 (HTTP status code)
    const numCode = Number(err.code);
    const bussinessErr = numCode === 12000001 || numCode === 12000002 || numCode === 401;
    if (!import.meta.env.SSR && bussinessErr) {
      emitEvtBus(Events.LOGOUT);
      throw err;
    }

    // Other error codes go through responseGuards config
    if (err.code && responseGuards[err.code]) {
      const result = responseGuards[err.code](err);

      // Check if this is a specially formatted error object
      if (result && typeof result === 'object' && 'error' in result && result.error === true) {
        console.log('server error');
        // Convert specially formatted error object to a friendly error message
        throw new ServerError(result.message || 'Operation failed', Number(result.code) || 0, err.url || '', result);
      }

      return result; // Return processed result
    } else {
      throw err;
    }
  }
}
